import type { Point } from "./types";

interface OrientationReading {
  alpha: number | null;
  absolute: boolean;
  /** Safari only: degrees clockwise from magnetic north. */
  webkitCompassHeading?: number;
}

const FULL_TURN = 360;

const normalizeAngle = (angle: number): number => ((angle % FULL_TURN) + FULL_TURN) % FULL_TURN;

/** Degrees clockwise from north that the top of the screen points to, or `undefined` without an absolute reading. */
const headingFromOrientation = (reading: OrientationReading, screenAngle: number): number | undefined => {
  if (reading.webkitCompassHeading !== undefined) return normalizeAngle(reading.webkitCompassHeading + screenAngle);
  if (!reading.absolute || reading.alpha === null) return undefined;
  return normalizeAngle(FULL_TURN - reading.alpha + screenAngle);
};

/** Moves `previous` towards `next` along the shortest arc, so the result never jumps a full turn at 0°/360°. */
const smoothAngle = (previous: number, next: number, factor: number): number => {
  const delta = ((((next - previous) % FULL_TURN) + 540) % FULL_TURN) - 180;
  return previous + delta * factor;
};

const planRotation = (northAngle: number, heading: number): number => northAngle - heading;

/** Where a point seen on a plan turned by `rotation` degrees around `center` lies on the unturned plan. */
const unrotatePoint = (point: Point, center: Point, rotation: number): Point => {
  const radians = (-rotation * Math.PI) / 180;
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * Math.cos(radians) - dy * Math.sin(radians),
    y: center.y + dx * Math.sin(radians) + dy * Math.cos(radians),
  };
};

export { headingFromOrientation, normalizeAngle, planRotation, smoothAngle, unrotatePoint };
export type { OrientationReading };
